// src/pages/LoadsPage.js
import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Chip, LinearProgress, FormControl, InputLabel, Select, Tooltip, Checkbox,
  Stack, TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  CloudUpload as UploadIcon, Visibility as ViewIcon,
  PlayArrow as PlayIcon, Stop as StopIcon,
  GetApp as DownloadIcon, FilterList as FilterListIcon,
  Tune as TuneIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import * as XLSX from 'xlsx';
import { useRef } from 'react';

const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info', 
  completed: 'success',
  error: 'error'
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'Procesando',
  completed: 'Completada',
  error: 'Error'
};

const DEFAULT_FILTERS = {
  campaign_id: '',
  skill_id: '',
  start_date: '',
  end_date: '',
  load_user: '',
  search: ''
};

const COLUMN_OPTIONS = [
  { id: 'name', label: 'Nombre', sortable: true, filterable: true, type: 'string' },
  { id: 'campaign_name', label: 'Campaña', sortable: true, filterable: true, type: 'string' },
  { id: 'skill_name', label: 'Skill', sortable: true, filterable: true, type: 'string' },
  { id: 'status', label: 'Estado', sortable: true, filterable: true, type: 'string' },
  { id: 'total_records', label: 'Registros', sortable: true, filterable: true, type: 'number' },
  { id: 'file_name', label: 'Archivo', sortable: true, filterable: true, type: 'string' },
  { id: 'load_date', label: 'Fecha', sortable: true, filterable: true, type: 'datetime' },
  { id: 'load_user', label: 'Usuario', sortable: true, filterable: true, type: 'string' },
  { id: 'actions', label: 'Acciones', sortable: false, filterable: false, type: 'string', align: 'right' }
];

export default function LoadsPage() {
  const { loads, campaigns, skills } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  const [items, setItems] = useState([]);
  const [campaignsList, setCampaignsList] = useState([]);
  const [campaignSkills, setCampaignSkills] = useState([]);
  const [filterSkills, setFilterSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activationLoadingId, setActivationLoadingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  const [open, setOpen] = useState(false);
  const [viewLoad, setViewLoad] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [debouncedFilters, setDebouncedFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [skillStructure, setSkillStructure] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingDraft, setMappingDraft] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationInfo, setValidationInfo] = useState(null);
  const [showValidationPreview, setShowValidationPreview] = useState(false);
  const pollingRef = useRef(null);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      campaign_id: '',
      skill_id: '',
      file: null
    }
  });
  const fileRegister = register('file', { required: 'Debe seleccionar un archivo' });

  const selectedCampaignId = watch('campaign_id');
  const selectedSkillId = watch('skill_id');


  const loadAll = useCallback(async (overrideFilters) => {
    try {
      setLoading(true);
      const activeFilters = overrideFilters || filters;
      const params = Object.entries(activeFilters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const loadsRes = await loads.getAll(params);
      const loadList = loadsRes?.data?.loads || [];
      setItems(loadList);
      setSelectedIds(prev =>
        prev.filter(id => loadList.some(item => String(item.id) === id))
      );
    } catch (e) {
      console.error('Error loading data:', e);
      showError(`Error cargando datos: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filters, loads, showError]);

  const loadCatalogs = useCallback(async () => {
    try {
      const [campaignsRes, skillsRes] = await Promise.all([
        campaigns.getAll(),
        skills.getAll()
      ]);

      setCampaignsList(campaignsRes?.data?.campaigns || []);
      setFilterSkills(skillsRes?.data?.skills || []);
    } catch (e) {
      console.error('Error loading catalogs:', e);
      showError(`Error cargando catálogos: ${e.message}`);
    }
  }, [campaigns, showError, skills]);

  const loadCampaignSkills = useCallback(async (campaignId) => {
    if (!campaignId) {
      setCampaignSkills([]);
      setValue('skill_id', '');
      return;
    }

    try {
      const response = await loads.getCampaignSkills(campaignId);
      setCampaignSkills(response?.data?.skills || []);
      setValue('skill_id', '');
    } catch (e) {
      console.error('Error loading campaign skills:', e);
      showError(`Error cargando skills: ${e.message}`);
      setCampaignSkills([]);
    }
  }, [loads, setValue, showError]);

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'campaign_id') {
        next.skill_id = '';
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    const resetFilters = { ...DEFAULT_FILTERS };
    setFilters(resetFilters);
    setDebouncedFilters(resetFilters);
    setSelectedIds([]);
    loadAll(resetFilters);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);
    return () => clearTimeout(handler);
  }, [filters]);

  useEffect(() => {
    loadAll(debouncedFilters);
  }, [debouncedFilters, loadAll]);

  // Auto-refresco mientras existan cargas en proceso
  useEffect(() => {
    const hasProcessing = items.some(item => item.status === 'processing');
    if (hasProcessing && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        loadAll(filters);
      }, 5000);
    }
    if (!hasProcessing && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [items, loadAll, filters]);

  const handleActivationToggle = async (load, shouldActivate) => {
    try {
      setActivationLoadingId(load.id);
      await loads.setActivationState(load.id, shouldActivate);
      showSuccess(`Carga ${shouldActivate ? 'activada' : 'detenida'} correctamente.`);
      await loadAll();
    } catch (e) {
      console.error('Error updating load activation:', e);
      showError(`No se pudo actualizar la carga: ${e.message}`);
    } finally {
      setActivationLoadingId(null);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = sortedItems.map(item => String(item.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    const idValue = String(id);
    setSelectedIds((prev) =>
      prev.includes(idValue)
        ? prev.filter(selectedId => selectedId !== idValue)
        : [...prev, idValue]
    );
  };

  const handleBulkActivation = async (shouldActivate) => {
    if (!selectedIds.length) return;
    try {
      setBulkActionLoading(true);
      await loads.bulkSetActivationState(selectedIds, shouldActivate);
      showSuccess(`Cargas ${shouldActivate ? 'activadas' : 'detenidas'} correctamente.`);
      setSelectedIds([]);
      await loadAll();
    } catch (e) {
      console.error('Error bulk updating activation:', e);
      showError(`No se pudo actualizar la selección: ${e.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.length} carga(s)? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      setBulkActionLoading(true);
      await loads.bulkDelete(selectedIds);
      showSuccess('Cargas eliminadas correctamente.');
      setSelectedIds([]);
      await loadAll();
    } catch (e) {
      console.error('Error bulk deleting loads:', e);
      showError(`No se pudieron eliminar las cargas: ${e.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedSkillId) {
      showError('Selecciona un skill para descargar la plantilla.');
      return;
    }
    try {
      setTemplateLoading(true);
      const blob = await loads.downloadTemplate(selectedSkillId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `plantilla_skill_${selectedSkillId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading template:', e);
      showError(`No se pudo descargar la plantilla: ${e.message}`);
    } finally {
      setTemplateLoading(false);
    }
  };

  const buildAutoMapping = (columns = [], existingMapping = {}) => {
    if (!columns.length) return {};
    const normalizedColumns = columns.map(col => ({
      original: col,
      normalized: col.trim().toLowerCase()
    }));
    const mapping = {};
    skillStructure.forEach(field => {
      const normalizedField = field.name.trim().toLowerCase();
      const directMatch = normalizedColumns.find(col => col.normalized === normalizedField);
      if (directMatch) {
        mapping[field.name] = directMatch.original;
      } else if (existingMapping[field.name]) {
        const previousMatch = normalizedColumns.find(
          col => col.normalized === existingMapping[field.name].trim().toLowerCase()
        );
        if (previousMatch) {
          mapping[field.name] = previousMatch.original;
        }
      }
    });
    return mapping;
  };

  const handleFileSelection = async (event) => {
    const input = event.target;
    const fileList = input?.files;

    // Limpiar previsualización anterior
    setValidationInfo(null);
    setShowValidationPreview(false);
    setSelectedFile(null);

    if (!fileList || !fileList[0]) {
      setSelectedFileName('');
      setSelectedFile(null);
      return;
    }
    if (!selectedSkillId) {
      showError('Selecciona un skill antes de cargar un archivo.');
      if (input) {
        input.value = '';
      }
      setSelectedFileName('');
      return;
    }
    try {
      setSelectedFileName(fileList[0].name);
      setSelectedFile(fileList[0]); // Guardar referencia del archivo
      const arrayBuffer = await fileList[0].arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const headerRow = (rows[0] || [])
        .map(col => (col || '').toString().trim())
        .filter(Boolean);

      setColumnOptions(headerRow);
      const autoMapping = buildAutoMapping(headerRow, columnMapping);
      setColumnMapping(autoMapping);
      setMappingDraft(autoMapping);

      const needsMapping = skillStructure.some(field => {
        const hasExactColumn = headerRow.some(
          col => col.trim().toLowerCase() === field.name.trim().toLowerCase()
        );
        return !hasExactColumn && !autoMapping[field.name];
      });

      setMappingDialogOpen(needsMapping);

      // Validar y mostrar previsualización
      try {
        const validationResult = await validateFile(fileList[0], selectedSkillId);
        if (validationResult.success && validationResult.validation) {
          setValidationInfo(validationResult.validation);
          setShowValidationPreview(true);
        }
      } catch (e) {
        console.error('Error en previsualización:', e);
        // No mostramos error aquí porque la previsualización es opcional
      }
    } catch (e) {
      console.error('Error parsing file for mapping:', e);
      showError('No se pudo leer el archivo para mapear las columnas.');
      if (input) {
        input.value = '';
      }
      setColumnOptions([]);
      setColumnMapping({});
      setMappingDraft({});
      setSelectedFileName('');
      setSelectedFile(null);
      setValidationInfo(null);
      setShowValidationPreview(false);
    }
  };

  const handleMappingDialogOpen = () => {
    if (!columnOptions.length) {
      showError('Selecciona un archivo para analizar las columnas.');
      return;
    }
    setMappingDraft({ ...columnMapping });
    setMappingDialogOpen(true);
  };

  const handleMappingDialogClose = () => {
    setMappingDialogOpen(false);
  };

  const handleMappingChange = (fieldName) => (event) => {
    const value = event.target.value;
    setMappingDraft(prev => ({
      ...prev,
      [fieldName]: value || ''
    }));
  };

  const handleMappingSave = () => {
    if (!columnOptions.length) {
      setMappingDialogOpen(false);
      return;
    }

    // Solo requerir asignación para campos de números de teléfono
    const phoneFields = skillStructure.filter(field =>
      field.number_mapping && field.number_mapping.startsWith('number')
    );

    const missingPhoneAssignments = phoneFields.filter(field => {
      const hasExactColumn = columnOptions.some(
        col => col.trim().toLowerCase() === field.name.trim().toLowerCase()
      );
      const mappedColumn = mappingDraft[field.name];
      // Un campo puede estar asignado a una columna real o marcado como "__NULL__"
      return !hasExactColumn && !mappedColumn;
    });

    // Verificar que al menos un campo de teléfono esté asignado a una columna real (no __NULL__)
    const assignedPhoneFields = phoneFields.filter(field => {
      const hasExactColumn = columnOptions.some(
        col => col.trim().toLowerCase() === field.name.trim().toLowerCase()
      );
      const mappedColumn = mappingDraft[field.name];
      // Solo contar asignaciones a columnas reales, no las marcadas como __NULL__
      return hasExactColumn || (mappedColumn && mappedColumn !== '__NULL__');
    });

    if (assignedPhoneFields.length === 0) {
      showError('Debes asignar al menos un campo de número de teléfono.');
      return;
    }

    setColumnMapping({ ...mappingDraft });
    setMappingDialogOpen(false);

    // Re-validar archivo con el nuevo mapeo de columnas
    if (selectedFile && selectedSkillId) {
      const reValidateFile = async () => {
        try {
          const validationResult = await validateFile(selectedFile, selectedSkillId, mappingDraft);
          if (validationResult.success && validationResult.validation) {
            setValidationInfo(validationResult.validation);
            setShowValidationPreview(true);
          }
        } catch (e) {
          console.error('Error en re-validación:', e);
        }
      };
      reValidateFile();
    }
  };

  const skillFilterOptions = useMemo(() => {
    if (!filters.campaign_id) {
      return filterSkills;
    }
    return filterSkills.filter(
      (skill) => String(skill.idcampaign) === String(filters.campaign_id)
    );
  }, [filterSkills, filters.campaign_id]);

  const selectionCount = selectedIds.length;
  const handleSortChange = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const getColumnFilterString = (row, column) => {
    switch (column) {
      case 'name':
        return row.name || '';
      case 'campaign_name':
        return row.campaign_name || '';
      case 'skill_name':
        return row.skill_name || '';
      case 'status':
        return STATUS_LABELS[row.status] || row.status || '';
      case 'total_records':
        return String(row.total_records ?? row.processed_records ?? '');
      case 'file_name':
        return row.file_name || '';
      case 'load_date':
        return row.load_date ? formatDate(row.load_date) : '';
      case 'load_user':
        return row.load_user || '';
      default:
        return '';
    }
  };

  const columnFilteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters)
      .filter(([column, value]) => COLUMN_OPTIONS.some(opt => opt.id === column && opt.filterable) && value?.trim());
    if (!activeFilters.length) {
      return items;
    }
    return items.filter(row =>
      activeFilters.every(([column, value]) => {
        const normalized = value.trim().toLowerCase();
        const candidate = getColumnFilterString(row, column).toLowerCase();
        return candidate.includes(normalized);
      })
    );
  }, [columnFilters, items]);

  const sortedItems = useMemo(() => {
    if (!sortConfig.column) {
      return columnFilteredItems;
    }
    const columnType = COLUMN_OPTIONS.find(opt => opt.id === sortConfig.column)?.type || 'string';
    const data = [...columnFilteredItems];
    const getSortValue = (row) => {
      switch (sortConfig.column) {
        case 'name':
          return row.name || '';
        case 'campaign_name':
          return row.campaign_name || '';
        case 'skill_name':
          return row.skill_name || '';
        case 'status':
          return STATUS_LABELS[row.status] || row.status || '';
        case 'total_records':
          return Number(row.total_records ?? row.processed_records ?? 0);
        case 'file_name':
          return row.file_name || '';
        case 'load_date':
          return row.load_date ? new Date(row.load_date).getTime() : 0;
        case 'load_user':
          return row.load_user || '';
        default:
          return '';
      }
    };
    data.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (columnType === 'number' || columnType === 'datetime') {
        const diff = (Number(valueA) || 0) - (Number(valueB) || 0);
        return sortConfig.direction === 'asc' ? diff : -diff;
      }
      const stringA = (valueA || '').toString().toLowerCase();
      const stringB = (valueB || '').toString().toLowerCase();
      if (stringA === stringB) return 0;
      if (stringA > stringB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return sortConfig.direction === 'asc' ? -1 : 1;
    });
    return data;
  }, [columnFilteredItems, sortConfig]);

  const allVisibleSelected = sortedItems.length > 0 && sortedItems.every(item => selectedIds.includes(String(item.id)));
  const isSelectionIndeterminate = selectionCount > 0 && !allVisibleSelected;

  const onSubmit = async (data) => {
    try {
      if (!data.file || !data.file[0]) {
        showError('Debe seleccionar un archivo');
        return;
      }

      if (columnOptions.length && skillStructure.length) {
        // Solo requerir mapeo para campos de números de teléfono si no tienen coincidencia exacta
        const phoneFields = skillStructure.filter(field =>
          field.number_mapping && field.number_mapping.startsWith('number')
        );

        const unmappedPhoneFields = phoneFields.filter(field => {
          const hasExactColumn = columnOptions.some(
            col => col.trim().toLowerCase() === field.name.trim().toLowerCase()
          );
          const mappedColumn = columnMapping[field.name];
          return !hasExactColumn && !mappedColumn;
        });

        if (unmappedPhoneFields.length > 0) {
          showError(`Debes asignar al menos un campo de número de teléfono. Campos sin asignar: ${unmappedPhoneFields.map(f => f.name).join(', ')}`);
          setMappingDialogOpen(true);
          return;
        }

        // Verificar que al menos un campo de teléfono esté asignado a una columna real (no __NULL__)
        const mappedPhoneFields = phoneFields.filter(field => {
          const hasExactColumn = columnOptions.some(
            col => col.trim().toLowerCase() === field.name.trim().toLowerCase()
          );
          const mappedColumn = columnMapping[field.name];
          return hasExactColumn || (mappedColumn && mappedColumn !== '__NULL__');
        });

        if (mappedPhoneFields.length === 0) {
          showError('Debes asignar al menos un campo de número de teléfono para poder cargar los datos.');
          setMappingDialogOpen(true);
          return;
        }
      }

      // Validar archivo antes de proceder
      showSuccess('Validando archivo...');
      const validationResult = await validateFile(data.file[0], data.skill_id, columnMapping);

      if (!validationResult.success || !validationResult.validation.valid) {
        const errors = validationResult.validation?.sample_errors || validationResult.validation?.errors || [];
        const warnings = validationResult.validation?.warnings || [];

        let errorMessage = 'El archivo tiene errores:\n';
        if (errors.length > 0) {
          errorMessage += '\nErrores:\n' + errors.slice(0, 5).join('\n');
          if (errors.length > 5) {
            errorMessage += `\n... y ${errors.length - 5} errores más`;
          }
        }
        if (warnings.length > 0) {
          errorMessage += '\n\nAdvertencias:\n' + warnings.slice(0, 5).join('\n');
        }

        showError(errorMessage);
        return;
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('campaign_id', data.campaign_id);
      formData.append('skill_id', data.skill_id);
      formData.append('file', data.file[0]);
      formData.append('load_user', 'admin'); // TODO: obtener usuario actual
      if (columnOptions.length > 0 && Object.keys(columnMapping).length > 0) {
        formData.append('column_mapping', JSON.stringify(columnMapping));
      }

      await loads.create(formData);

      showSuccess('Carga iniciada correctamente. El procesamiento se realizará en segundo plano.');
      setOpen(false);
      reset();
      setSelectedFileName('');
      setSelectedFile(null);
      setColumnOptions([]);
      setColumnMapping({});
      setMappingDraft({});
      setMappingDialogOpen(false);
      setValidationInfo(null);
      setShowValidationPreview(false);
      await loadAll();
    } catch (e) {
      console.error('Error creating load:', e);
      showError(`Error creando carga: ${e.message}`);
    }
  };

  const deleteLoad = async (load) => {
    if (!window.confirm(`¿Eliminar carga "${load.name}"?`)) return;
    
    try {
      await loads.delete(load.id);
      showSuccess('Carga eliminada');
      setSelectedIds(prev => prev.filter(id => id !== String(load.id)));
      await loadAll();
    } catch (e) {
      console.error('Error deleting load:', e);
      showError(`Error eliminando carga: ${e.message}`);
    }
  };

  const viewLoadDetails = async (load) => {
    try {
      const response = await loads.getById(load.id);
      setViewLoad(response.data.load);
      setOpenView(true);
    } catch (e) {
      console.error('Error loading load details:', e);
      showError(`Error cargando detalle: ${e.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-ES');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const downloadErrors = async (loadId) => {
    try {
      const response = await fetch(`/api/admin/loads/${loadId}/errors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error descargando archivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `errores_carga_${loadId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Error downloading errors:', e);
      showError(`Error descargando errores: ${e.message}`);
    }
  };

  const validateFile = async (file, skillId, columnMappingParam = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('skill_id', skillId);

      // Si se proporciona mapeo de columnas, incluirlo
      if (columnMappingParam && Object.keys(columnMappingParam).length > 0) {
        formData.append('column_mapping', JSON.stringify(columnMappingParam));
      }

      const response = await fetch('/api/admin/loads/validate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();
      return result;
    } catch (e) {
      console.error('Error validating file:', e);
      return {
        success: false,
        validation: {
          valid: false,
          errors: [`Error validando archivo: ${e.message}`]
        }
      };
    }
  };

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  // Cargar skills cuando cambia la campaña seleccionada
  useEffect(() => {
    loadCampaignSkills(selectedCampaignId);
  }, [loadCampaignSkills, selectedCampaignId]);

  useEffect(() => {
    if (!selectedSkillId) {
      setSkillStructure([]);
      setColumnOptions([]);
      setColumnMapping({});
      setMappingDraft({});
      setMappingDialogOpen(false);
      setValidationInfo(null);
      setShowValidationPreview(false);
      setSelectedFile(null);
      return;
    }

    const fetchStructure = async () => {
      try {
        const response = await loads.getSkillStructure(selectedSkillId);
        setSkillStructure(response?.data?.fields || []);
        setColumnMapping({});
        setMappingDraft({});
        setColumnOptions([]);
        setMappingDialogOpen(false);
        setValidationInfo(null);
        setShowValidationPreview(false);
        setSelectedFile(null);
      } catch (e) {
        console.error('Error fetching skill structure:', e);
        showError(`Error obteniendo estructura: ${e.message}`);
        setSkillStructure([]);
      }
    };

    fetchStructure();
  }, [selectedSkillId, loads, showError]);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" component="h1">
                  Gestión de Cargas
                </Typography>
                <Box>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => loadAll(filters)}
                    sx={{ mr: 1 }}
                    disabled={loading}
                  >
                    Actualizar
                  </Button>
                  {canAction('upload_load') && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setOpen(true);
                        // Limpiar estado de previsualización al abrir el diálogo
                        setValidationInfo(null);
                        setShowValidationPreview(false);
                        setSelectedFileName('');
                        setSelectedFile(null);
                      }}
                    >
                      Nueva Carga
                    </Button>
                  )}
                </Box>
              </Box>

              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Campaña</InputLabel>
                      <Select
                        value={filters.campaign_id}
                        label="Campaña"
                        onChange={handleFilterChange('campaign_id')}
                      >
                        <MenuItem value="">Todas</MenuItem>
                        {campaignsList.map((campaign) => (
                          <MenuItem key={campaign.idcampaign} value={campaign.idcampaign}>
                            {campaign.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Skill</InputLabel>
                      <Select
                        value={filters.skill_id}
                        label="Skill"
                        onChange={handleFilterChange('skill_id')}
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {skillFilterOptions.map((skill) => (
                          <MenuItem key={skill.id} value={skill.id}>
                            {skill.skillname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Desde"
                      type="date"
                      value={filters.start_date}
                      onChange={handleFilterChange('start_date')}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Hasta"
                      type="date"
                      value={filters.end_date}
                      onChange={handleFilterChange('end_date')}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Usuario"
                      value={filters.load_user}
                      onChange={handleFilterChange('load_user')}
                      fullWidth
                      size="small"
                      placeholder="Responsable"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Buscar por nombre"
                      value={filters.search}
                      onChange={handleFilterChange('search')}
                      fullWidth
                      size="small"
                      placeholder="Nombre de la carga"
                    />
                  </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button onClick={handleClearFilters}>
                    Limpiar filtros
                  </Button>
                </Box>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Seleccionadas: {selectionCount}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {canAction('bulk_operations_load') && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!selectionCount || bulkActionLoading}
                      onClick={() => handleBulkActivation(true)}
                    >
                      Activar seleccionadas
                    </Button>
                  )}
                  {canAction('bulk_operations_load') && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!selectionCount || bulkActionLoading}
                      onClick={() => handleBulkActivation(false)}
                    >
                      Detener seleccionadas
                    </Button>
                  )}
                  {canAction('bulk_operations_load') && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      disabled={!selectionCount || bulkActionLoading}
                      onClick={handleBulkDelete}
                    >
                      Eliminar seleccionadas
                    </Button>
                  )}
                </Box>
              </Box>

              {loading && <LinearProgress sx={{ mb: 2 }} />}

              <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 0,
                    minWidth: '100%',
                    overflowX: 'auto'
                  }}
                >
                <Table sx={{ minWidth: 'max-content' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={isSelectionIndeterminate}
                          checked={allVisibleSelected}
                          onChange={handleSelectAll}
                          inputProps={{ 'aria-label': 'Seleccionar todas las cargas' }}
                        />
                      </TableCell>
                      {COLUMN_OPTIONS.map((column) => (
                        <TableCell key={column.id} align={column.align || 'left'}>
                          <Stack spacing={0.5}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {column.sortable ? (
                                <TableSortLabel
                                  active={sortConfig.column === column.id}
                                  direction={sortConfig.column === column.id ? sortConfig.direction : 'asc'}
                                  onClick={() => handleSortChange(column.id)}
                                >
                                  {column.label}
                                </TableSortLabel>
                              ) : (
                                <Typography variant="subtitle2">{column.label}</Typography>
                              )}
                              {column.filterable && (
                                <IconButton
                                  size="small"
                                  color={columnFilters[column.id] ? 'primary' : 'default'}
                                  onClick={() =>
                                    setActiveFilterColumn(prev => (prev === column.id ? null : column.id))
                                  }
                                >
                                  <FilterListIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                            {column.filterable && activeFilterColumn === column.id && (
                              <TextField
                                value={columnFilters[column.id] || ''}
                                onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
                                size="small"
                                placeholder="Filtrar"
                                variant="standard"
                                autoFocus
                                onBlur={() => setActiveFilterColumn(null)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    setActiveFilterColumn(null);
                                  }
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedItems.map((item) => {
                      const isActivationLoading = activationLoadingId === item.id;
                      const isLoadActive = item.active === undefined ? true : Number(item.active) === 1;
                      const processedCount = item.processed_records ?? '-';
                      const failedCount = item.failed_records ?? 0;
                      const showFailed = Number(failedCount) > 0;
                      const rowId = String(item.id);
                      const isSelected = selectedIds.includes(rowId);
                      return (
                        <TableRow key={item.id} hover selected={isSelected}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectRow(item.id)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">{item.name}</Typography>
                          </TableCell>
                          <TableCell>
                            {item.campaign_name || '-'}
                          </TableCell>
                          <TableCell>
                            {item.skill_name}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_LABELS[item.status] || item.status}
                              color={STATUS_COLORS[item.status] || 'default'}
                              size="small"
                            />
                            {item.status === 'completed' && item.failed_records > 0 && (
                              <Tooltip title={`${item.failed_records} registros con errores - Click en ver detalles para descargar`}>
                                <Chip
                                  label={`${item.failed_records} errores`}
                                  color="error"
                                  size="small"
                                  style={{ marginLeft: 8 }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.status === 'completed' ? (
                              <Box>
                                <Typography variant="body2" color="success.main">
                                  Procesados: {processedCount}
                                </Typography>
                                {showFailed && (
                                  <Typography variant="body2" color="error.main">
                                    Fallidos: {failedCount}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2">
                                {item.total_records || '-'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.file_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(item.file_size)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(item.load_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.load_user}
                          </TableCell>
                          <TableCell align="right">
                            {canAction('activate_load') && (
                              <Tooltip title={isLoadActive ? 'Detener carga' : 'Activar carga'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={isLoadActive ? 'warning' : 'success'}
                                    onClick={() => handleActivationToggle(item, !isLoadActive)}
                                    disabled={isActivationLoading}
                                  >
                                    {isLoadActive ? <StopIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <IconButton size="small" onClick={() => viewLoadDetails(item)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            {canAction('delete_load') && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteLoad(item)}
                                disabled={item.status === 'processing'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {sortedItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          No hay cargas registradas
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>

                </Table>
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog Nueva Carga */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Nueva Carga de Datos</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Nombre de la Carga"
                  fullWidth
                  {...register('name', { required: 'El nombre es requerido' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Campaña</InputLabel>
                  <Select
                    {...register('campaign_id')}
                    label="Campaña"
                    value={watch('campaign_id') || ''}
                    onChange={(e) => setValue('campaign_id', e.target.value)}
                  >
                    <MenuItem value="">Sin campaña</MenuItem>
                    {campaignsList.map((campaign) => (
                      <MenuItem key={campaign.idcampaign} value={campaign.idcampaign}>
                        {campaign.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Skill</InputLabel>
                  <Select
                    {...register('skill_id', { required: 'El skill es requerido' })}
                    label="Skill"
                    value={watch('skill_id') || ''}
                    onChange={(e) => setValue('skill_id', e.target.value)}
                    error={!!errors.skill_id}
                  >
                    {campaignSkills.map((skill) => (
                      <MenuItem key={skill.id} value={skill.id}>
                        {skill.skillname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.skill_id && (
                  <Typography variant="caption" color="error">
                    {errors.skill_id.message}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                  disabled={!selectedSkillId}
                  sx={{
                    height: 90,
                    justifyContent: 'flex-start',
                    p: 2,
                    borderStyle: 'dashed',
                    borderColor: selectedFileName ? 'success.main' : 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon sx={{ color: selectedFileName ? 'success.main' : 'text.secondary', fontSize: 40 }} />
                      <Box>
                        <Typography variant="subtitle2">
                          {selectedFileName ? 'Archivo seleccionado' : 'Seleccionar archivo Excel'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedFileName || 'Formatos permitidos: .xlsx, .xls, .csv'}
                        </Typography>
                      </Box>
                    </Box>
                    {selectedFileName && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedFileName}
                      </Typography>
                    )}
                  </Box>
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls,.csv"
                    {...fileRegister}
                    onChange={(event) => {
                      fileRegister.onChange(event);
                      handleFileSelection(event);
                    }}
                  />
                </Button>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Button
                    variant="text"
                    startIcon={<DownloadIcon />}
                    disabled={!selectedSkillId || templateLoading}
                    onClick={handleDownloadTemplate}
                  >
                    {templateLoading ? 'Descargando…' : 'Descargar plantilla'}
                  </Button>
                <Button
                  variant="text"
                  startIcon={<TuneIcon />}
                  disabled={!columnOptions.length || !selectedSkillId}
                  onClick={handleMappingDialogOpen}
                >
                  Mapear columnas
                </Button>
                </Box>
                {errors.file && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.file.message}
                  </Typography>
                )}

                {/* Previsualización de datos */}
                {showValidationPreview && validationInfo && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Previsualización del Archivo
                    </Typography>

                    <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Total de registros:</Typography>
                          <Typography variant="h6">{validationInfo.total_rows}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Registros válidos:</Typography>
                          <Typography variant="h6" color="success.main">{validationInfo.valid_rows}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Registros inválidos:</Typography>
                          <Typography variant="h6" color="error.main">{validationInfo.invalid_rows}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Estado:</Typography>
                          <Chip
                            label={validationInfo.valid ? 'Válido' : 'Con errores'}
                            color={validationInfo.valid ? 'success' : 'warning'}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {validationInfo.sample_data && validationInfo.sample_data.length > 0 && (
                      <Paper variant="outlined" sx={{ mb: 2, maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>#</TableCell>
                              {validationInfo.columns && validationInfo.columns.map((column) => (
                                <TableCell key={column.name} sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                                  {column.name}
                                  {column.number_mapping && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      {column.number_mapping}
                                    </Typography>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {validationInfo.sample_data.map((row, index) => (
                              <TableRow key={index} hover>
                                <TableCell>{row._rowNumber}</TableCell>
                                {validationInfo.columns && validationInfo.columns.map((column) => (
                                  <TableCell key={column.name}>
                                    <Typography variant="body2" sx={{
                                      maxWidth: 150,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {row[column.name] || ''}
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {validationInfo.total_rows > 10 && (
                          <Box sx={{ p: 1, backgroundColor: 'grey.50', textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Mostrando primeras 10 filas de {validationInfo.total_rows} registros
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    )}

                    {validationInfo.sample_errors && validationInfo.sample_errors.length > 0 && (
                      <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 1, backgroundColor: 'transparent' }}>
                        <Typography variant="subtitle2" color="error.main" gutterBottom>
                          Errores encontrados:
                        </Typography>
                        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                          {validationInfo.sample_errors.slice(0, 5).map((error, index) => (
                            <Typography key={index} variant="body2" color="error.main" sx={{ fontSize: '0.875rem' }}>
                              • {error}
                            </Typography>
                          ))}
                          {validationInfo.sample_errors.length > 5 && (
                            <Typography variant="body2" color="error.main" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                              ... y {validationInfo.sample_errors.length - 5} errores más
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => setShowValidationPreview(false)}
                      >
                        Ocultar previsualización
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpen(false);
              setSelectedFileName('');
              setSelectedFile(null);
              setColumnOptions([]);
              setColumnMapping({});
              setMappingDraft({});
              setValidationInfo(null);
              setShowValidationPreview(false);
            }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={!validationInfo || loading || processing}>
              Subir y Procesar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Ver Detalle */}
      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de Carga</DialogTitle>
        <DialogContent>
          {viewLoad && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Nombre:</Typography>
                <Typography>{viewLoad.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Estado:</Typography>
                <Chip
                  label={STATUS_LABELS[viewLoad.status]}
                  color={STATUS_COLORS[viewLoad.status]}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Campaña:</Typography>
                <Typography>{viewLoad.campaign_name || 'Sin campaña'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Skill:</Typography>
                <Typography>{viewLoad.skill_name}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Total Registros:</Typography>
                <Typography>{viewLoad.total_records || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Procesados:</Typography>
                <Typography color="success.main">{viewLoad.processed_records || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Fallos:</Typography>
                <Typography color="error.main">{viewLoad.failed_records || '-'}</Typography>
              </Grid>
              {viewLoad.failed_records > 0 && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadErrors(viewLoad.id)}
                    size="small"
                  >
                    Descargar Registros con Errores
                  </Button>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Archivo:</Typography>
                <Typography>{viewLoad.file_name}</Typography>
                <Typography variant="caption">{formatFileSize(viewLoad.file_size)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Fecha de Carga:</Typography>
                <Typography>{formatDate(viewLoad.load_date)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Usuario:</Typography>
                <Typography>{viewLoad.load_user}</Typography>
              </Grid>
              {viewLoad.error_message && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="error">Error:</Typography>
                  <Typography color="error">{viewLoad.error_message}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mappingDialogOpen} onClose={handleMappingDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Mapear columnas del archivo</DialogTitle>
        <DialogContent dividers>
          {skillStructure.length === 0 ? (
            <Typography variant="body2">
              Selecciona un skill para obtener la estructura de datos.
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {skillStructure.map((field) => (
                <Grid item xs={12} md={6} key={field.name}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{field.name}</InputLabel>
                    <Select
                      label={field.name}
                      value={mappingDraft[field.name] || ''}
                      onChange={handleMappingChange(field.name)}
                    >
                      <MenuItem value="">Coincide con el nombre del campo</MenuItem>
                      <MenuItem value="__NULL__" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        No existe en el archivo (vacío)
                      </MenuItem>
                      {columnOptions.map((column) => (
                        <MenuItem key={column} value={column}>
                          {column}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary">
                      {field.type}{field.number_mapping ? ` · ${field.number_mapping}` : ''}
                    </Typography>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMappingDialogClose}>Cancelar</Button>
          <Button
            onClick={handleMappingSave}
            variant="contained"
            disabled={!columnOptions.length}
          >
            Guardar asignaciones
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
